import { z } from 'zod';
import { type CardDetailsModel, CardDetailsModelSchema } from './ipg-card-details-model.types';
import type { DeepPartial } from '../../backend_config.types';
import type {
    CardPointeIpgConfig,
    CardPointeIpgConfigCustomization,
    CardPointeIpgConfigV4,
    CardPointeIpgConfigV7,
} from './cardpointe/cardpointe-backend-config.types';
import type {
    CloverIpgConfig,
    CloverIpgConfigCustomization,
} from './clover/clover-backend-config.types';
import { CloverTerminalTransport } from './clover/clover-backend-config.types';
import { CloverGatewayEnvironmentSchema } from '../../schemas/ipg/clover/clover-gateway-environment.schemas';
import { CardPointeGatewayEnvironmentSchema } from '../../schemas/ipg/cardpointe/cardpointe-gateway-environment.schemas';
import { CloverGatewayEnvironment } from './clover/clover-gateway-environment.types';
import type {
    ValorIpgConfig,
    ValorIpgConfigCustomization,
} from './valor/valor-backend-config.types';
import { type IpgSystemId, type IpgSystemIdKey } from '../third-party-system.enums';
import {
    CardPointeIpgConfigDemo,
    CardPointeIpgConfigProd,
} from './cardpointe/cardpointe-backend-config.constants';
import {
    CardPointeIpgSystemId,
    CardPointeIpgSystemIdKey,
} from './cardpointe/cardpointe-system-constants';
import { CloverIpgConfigDemo, CloverIpgConfigProd } from './clover/clover-backend-config.constants';
import { CloverIpgSystemId, CloverIpgSystemIdKey } from './clover/clover-system-constants';
import { ValorIpgConfigDemo, ValorIpgConfigProd } from './valor/valor-backend-config.constants';
import {
    ValorIpgConfigurationMode,
    ValorIpgSystemId,
    ValorIpgSystemIdKey,
} from './valor/valor-system-constants';

export const defaultIpgConfigByKey: Record<IpgSystemIdKey, IpgConfig> = {
    [CardPointeIpgSystemIdKey]: CardPointeIpgConfigProd,
    [CloverIpgSystemIdKey]: CloverIpgConfigProd,
    [ValorIpgSystemIdKey]: ValorIpgConfigProd,
};

export const defaultDemoIpgConfigByKey: Partial<Record<IpgSystemIdKey, IpgConfig>> = {
    [CardPointeIpgSystemIdKey]: CardPointeIpgConfigDemo,
    [CloverIpgSystemIdKey]: CloverIpgConfigDemo,
    [ValorIpgSystemIdKey]: ValorIpgConfigDemo,
};

export const defaultIpgConfigById: Record<IpgSystemId, IpgConfig> = {
    [CardPointeIpgSystemId]: CardPointeIpgConfigProd,
    [CloverIpgSystemId]: CloverIpgConfigProd,
    [ValorIpgSystemId]: ValorIpgConfigProd,
};

export type IpgConfig = ValorIpgConfig | CardPointeIpgConfig | CloverIpgConfig;

export interface IpgConfigStorageMap {
    readonly [ValorIpgSystemIdKey]?: DeepPartial<ValorIpgConfig>;
    readonly [CardPointeIpgSystemIdKey]?: DeepPartial<CardPointeIpgConfig>;
    readonly [CloverIpgSystemIdKey]?: DeepPartial<CloverIpgConfig>;
}

export interface IpgConfigStorageMapV4 {
    readonly [ValorIpgSystemIdKey]?: DeepPartial<ValorIpgConfig>;
    readonly [CardPointeIpgSystemIdKey]?: DeepPartial<CardPointeIpgConfigV4>;
    readonly [CloverIpgSystemIdKey]?: DeepPartial<CloverIpgConfig>;
}

export interface IpgConfigStorageMapV6 {
    readonly [ValorIpgSystemIdKey]?: DeepPartial<Omit<ValorIpgConfig, 'card_details_model'>>;
    readonly [CardPointeIpgSystemIdKey]?: DeepPartial<
        Omit<CardPointeIpgConfig, 'card_details_model'>
    >;
    readonly [CloverIpgSystemIdKey]?: DeepPartial<Omit<CloverIpgConfig, 'card_details_model'>>;
}

export interface IpgConfigStorageMapV7 {
    readonly [ValorIpgSystemIdKey]?: DeepPartial<ValorIpgConfig>;
    readonly [CardPointeIpgSystemIdKey]?: DeepPartial<CardPointeIpgConfigV7>;
    readonly [CloverIpgSystemIdKey]?: DeepPartial<CloverIpgConfig>;
}

export type IpgConfigCustomization =
    | ValorIpgConfigCustomization
    | CardPointeIpgConfigCustomization
    | CloverIpgConfigCustomization;

export interface IpgConfigCustomizationMap {
    [ValorIpgSystemIdKey]?: ValorIpgConfigCustomization;
    [CardPointeIpgSystemIdKey]?: CardPointeIpgConfigCustomization;
    [CloverIpgSystemIdKey]?: CloverIpgConfigCustomization;
}

export interface UpdateIpgConfigCustomizationMap {
    [ValorIpgSystemIdKey]?: ValorIpgConfigCustomization | null;
    [CardPointeIpgSystemIdKey]?: CardPointeIpgConfigCustomization | null;
    [CloverIpgSystemIdKey]?: CloverIpgConfigCustomization | null;
}

interface IpgConfigSchemaRegistryFactoryInput {
    readonly urlPatternSchema: z.ZodType<IpgConfig['urlPattern']>;
    readonly allowedWebsiteSchema: z.ZodType<IpgConfig['allowedWebsites'][number]>;
    readonly allowedWebsiteCustomizationSchema: z.ZodType<
        CardPointeIpgConfigCustomization['allowedWebsites'][number]
    >;
    readonly rpaModuleNameSchema: z.ZodType<IpgConfig['rpaModules'][number]>;
    readonly createDeepOptionalSchema: <T>(schema: z.ZodType<T>) => z.ZodType<DeepPartial<T>>;
}

interface IpgConfigSchemaRegistry {
    readonly schema: z.ZodType<IpgConfig>;
    readonly schemaById: {
        readonly [ValorIpgSystemId]: z.ZodType<ValorIpgConfig>;
        readonly [CardPointeIpgSystemId]: z.ZodType<CardPointeIpgConfig>;
        readonly [CloverIpgSystemId]: z.ZodType<CloverIpgConfig>;
    };
    readonly storageMapSchema: z.ZodType<IpgConfigStorageMap>;
    readonly storageMapV4Schema: z.ZodType<IpgConfigStorageMapV4>;
    readonly storageMapV6Schema: z.ZodType<IpgConfigStorageMapV6>;
    readonly storageMapV7Schema: z.ZodType<IpgConfigStorageMapV7>;
    readonly customizationSchema: z.ZodType<IpgConfigCustomization>;
    readonly customizationSchemaById: {
        readonly [ValorIpgSystemId]: z.ZodType<ValorIpgConfigCustomization>;
        readonly [CardPointeIpgSystemId]: z.ZodType<CardPointeIpgConfigCustomization>;
        readonly [CloverIpgSystemId]: z.ZodType<CloverIpgConfigCustomization>;
    };
    readonly customizationMapSchema: z.ZodType<IpgConfigCustomizationMap>;
    readonly updateCustomizationMapSchema: z.ZodType<UpdateIpgConfigCustomizationMap>;
}

type IpgConfigSharedSchemaShape = {
    readonly name: z.ZodString;
    readonly urlPattern: z.ZodType<IpgConfig['urlPattern']>;
    readonly workflow_prefix: z.ZodString;
    readonly allowedWebsites: z.ZodDefault<
        z.ZodArray<z.ZodType<IpgConfig['allowedWebsites'][number]>>
    >;
    readonly rpaModules: z.ZodDefault<z.ZodArray<z.ZodType<IpgConfig['rpaModules'][number]>>>;
    readonly card_details_model: z.ZodOptional<z.ZodType<CardDetailsModel>>;
};

type ValorIpgConfigSchemaShape = IpgConfigSharedSchemaShape & {
    readonly ipg_id: z.ZodLiteral<typeof ValorIpgSystemId>;
    readonly configuration_mode: z.ZodType<ValorIpgConfigurationMode>;
    readonly default_api_url: z.ZodOptional<z.ZodString>;
    readonly default_channel: z.ZodOptional<z.ZodString>;
};

type CardPointeIpgConfigSchemaShape = IpgConfigSharedSchemaShape & {
    readonly ipg_id: z.ZodLiteral<typeof CardPointeIpgSystemId>;
    readonly environment: typeof CardPointeGatewayEnvironmentSchema;
    readonly terminal_include_avs: z.ZodDefault<z.ZodBoolean>;
    readonly gateway_credential_uuid: z.ZodOptional<z.ZodString>;
};

type CardPointeIpgConfigV4SchemaShape = Omit<
    CardPointeIpgConfigSchemaShape,
    'gateway_credential_uuid' | 'terminal_include_avs'
>;

type CardPointeIpgConfigV7SchemaShape = Omit<
    CardPointeIpgConfigSchemaShape,
    'terminal_include_avs'
>;

type CloverIpgConfigSchemaShape = IpgConfigSharedSchemaShape & {
    readonly ipg_id: z.ZodLiteral<typeof CloverIpgSystemId>;
    readonly environment: typeof CloverGatewayEnvironmentSchema;
    readonly terminal_transport: z.ZodLiteral<typeof CloverTerminalTransport.CLOUD_PAY_DISPLAY>;
};

type IpgConfigCustomizationSharedSchemaShape = {
    readonly urlPattern: z.ZodType<CardPointeIpgConfigCustomization['urlPattern']>;
    readonly allowedWebsites: z.ZodArray<
        z.ZodType<CardPointeIpgConfigCustomization['allowedWebsites'][number]>
    >;
    readonly card_details_model: z.ZodOptional<typeof CardDetailsModelSchema>;
};

type ValorIpgConfigCustomizationSchemaShape = {
    readonly ipg_id: z.ZodLiteral<typeof ValorIpgSystemId>;
    readonly configuration_mode: z.ZodType<ValorIpgConfigurationMode>;
    readonly urlPattern: z.ZodOptional<IpgConfigCustomizationSharedSchemaShape['urlPattern']>;
    readonly allowedWebsites: z.ZodOptional<
        IpgConfigCustomizationSharedSchemaShape['allowedWebsites']
    >;
    readonly portalUrl: z.ZodOptional<z.ZodString>;
    readonly default_api_url: z.ZodOptional<z.ZodString>;
    readonly default_channel: z.ZodOptional<z.ZodString>;
    readonly card_details_model: z.ZodOptional<typeof CardDetailsModelSchema>;
};

type CardPointeIpgConfigCustomizationSchemaShape = IpgConfigCustomizationSharedSchemaShape & {
    readonly ipg_id: z.ZodLiteral<typeof CardPointeIpgSystemId>;
    readonly environment: typeof CardPointeGatewayEnvironmentSchema;
    readonly terminal_include_avs: z.ZodBoolean;
    readonly portalUrl: z.ZodOptional<z.ZodString>;
    readonly gateway_credential_uuid: z.ZodOptional<z.ZodNullable<z.ZodString>>;
};

type CloverIpgConfigCustomizationSchemaShape = {
    readonly ipg_id: z.ZodLiteral<typeof CloverIpgSystemId>;
    readonly environment: typeof CloverGatewayEnvironmentSchema;
    readonly card_details_model: z.ZodOptional<typeof CardDetailsModelSchema>;
};

export function createIpgConfigSchemaRegistry(
    input: IpgConfigSchemaRegistryFactoryInput
): IpgConfigSchemaRegistry {
    const sharedShape: IpgConfigSharedSchemaShape = {
        name: z.string(),
        urlPattern: input.urlPatternSchema,
        workflow_prefix: z.string(),
        allowedWebsites: z.array(input.allowedWebsiteSchema).default([]),
        rpaModules: z.array(input.rpaModuleNameSchema).default([]),
        card_details_model: CardDetailsModelSchema.optional(),
    };
    const valorSchema: z.ZodObject<ValorIpgConfigSchemaShape> = z
        .object({
            ...sharedShape,
            ipg_id: z.literal(ValorIpgSystemId),
            configuration_mode: z
                .nativeEnum(ValorIpgConfigurationMode)
                .default(ValorIpgConfigurationMode.PRODUCTION),
            default_api_url: z.string().url().optional(),
            default_channel: z.string().trim().min(1).optional(),
        })
        .strict();
    const cardPointeSchemaV4: z.ZodObject<CardPointeIpgConfigV4SchemaShape> = z
        .object({
            ...sharedShape,
            ipg_id: z.literal(CardPointeIpgSystemId),
            environment: CardPointeGatewayEnvironmentSchema,
        })
        .strict();
    const cardPointeSchemaV7: z.ZodObject<CardPointeIpgConfigV7SchemaShape> =
        cardPointeSchemaV4.extend({
            gateway_credential_uuid: z.string().uuid().optional(),
        });
    const cardPointeSchema: z.ZodObject<CardPointeIpgConfigSchemaShape> = cardPointeSchemaV7.extend(
        {
            terminal_include_avs: z.boolean().default(false),
        }
    );
    const cloverSchema: z.ZodObject<CloverIpgConfigSchemaShape> = z
        .object({
            ...sharedShape,
            ipg_id: z.literal(CloverIpgSystemId),
            environment: CloverGatewayEnvironmentSchema,
            terminal_transport: z.literal(CloverTerminalTransport.CLOUD_PAY_DISPLAY),
        })
        .strict();

    const schemaById = {
        [ValorIpgSystemId]: valorSchema,
        [CardPointeIpgSystemId]: cardPointeSchema,
        [CloverIpgSystemId]: cloverSchema,
    } as const;
    const schema: z.ZodType<IpgConfig> = z.discriminatedUnion('ipg_id', [
        valorSchema,
        cardPointeSchema,
        cloverSchema,
    ]);
    const storageMapSchema: z.ZodType<IpgConfigStorageMap> = z
        .object({
            [ValorIpgSystemIdKey]: input.createDeepOptionalSchema(valorSchema),
            [CardPointeIpgSystemIdKey]: input.createDeepOptionalSchema(cardPointeSchema),
            [CloverIpgSystemIdKey]: input.createDeepOptionalSchema(cloverSchema),
        })
        .partial()
        .strict();
    const storageMapV4Schema: z.ZodType<IpgConfigStorageMapV4> = z
        .object({
            [ValorIpgSystemIdKey]: input.createDeepOptionalSchema(valorSchema),
            [CardPointeIpgSystemIdKey]: input.createDeepOptionalSchema(cardPointeSchemaV4),
            [CloverIpgSystemIdKey]: input.createDeepOptionalSchema(cloverSchema),
        })
        .partial()
        .strict();
    const customizationSharedShape: IpgConfigCustomizationSharedSchemaShape = {
        urlPattern: input.urlPatternSchema,
        allowedWebsites: z.array(input.allowedWebsiteCustomizationSchema),
        card_details_model: CardDetailsModelSchema.optional(),
    };
    const valorCustomizationSchema: z.ZodObject<ValorIpgConfigCustomizationSchemaShape> = z
        .object({
            ipg_id: z.literal(ValorIpgSystemId),
            configuration_mode: z.nativeEnum(ValorIpgConfigurationMode),
            urlPattern: customizationSharedShape.urlPattern.optional(),
            allowedWebsites: customizationSharedShape.allowedWebsites.optional(),
            portalUrl: z.string().trim().url().optional(),
            default_api_url: z.string().url().optional(),
            default_channel: z.string().trim().min(1).optional(),
            card_details_model: CardDetailsModelSchema.optional(),
        })
        .strict();
    const storageMapV6Schema: z.ZodType<IpgConfigStorageMapV6> = z
        .object({
            [ValorIpgSystemIdKey]: input
                .createDeepOptionalSchema(valorSchema.omit({ card_details_model: true }))
                .optional(),
            [CardPointeIpgSystemIdKey]: input
                .createDeepOptionalSchema(cardPointeSchema.omit({ card_details_model: true }))
                .optional(),
            [CloverIpgSystemIdKey]: input
                .createDeepOptionalSchema(cloverSchema.omit({ card_details_model: true }))
                .optional(),
        })
        .strict();
    const storageMapV7Schema: z.ZodType<IpgConfigStorageMapV7> = z
        .object({
            [ValorIpgSystemIdKey]: input.createDeepOptionalSchema(valorSchema).optional(),
            [CardPointeIpgSystemIdKey]: input
                .createDeepOptionalSchema(cardPointeSchemaV7)
                .optional(),
            [CloverIpgSystemIdKey]: input.createDeepOptionalSchema(cloverSchema).optional(),
        })
        .strict();
    const cardPointeCustomizationSchema: z.ZodObject<CardPointeIpgConfigCustomizationSchemaShape> =
        z
            .object({
                ...customizationSharedShape,
                ipg_id: z.literal(CardPointeIpgSystemId),
                environment: CardPointeGatewayEnvironmentSchema,
                terminal_include_avs: z.boolean(),
                portalUrl: z.string().trim().url().optional(),
                gateway_credential_uuid: z.string().uuid().nullable().optional(),
            })
            .strict();
    const cloverCustomizationSchema: z.ZodObject<CloverIpgConfigCustomizationSchemaShape> = z
        .object({
            ipg_id: z.literal(CloverIpgSystemId),
            environment: CloverGatewayEnvironmentSchema,
            card_details_model: CardDetailsModelSchema.optional(),
        })
        .strict();
    const customizationSchema: z.ZodType<IpgConfigCustomization> = z.discriminatedUnion('ipg_id', [
        valorCustomizationSchema,
        cardPointeCustomizationSchema,
        cloverCustomizationSchema,
    ]);
    const customizationSchemaById = {
        [ValorIpgSystemId]: valorCustomizationSchema,
        [CardPointeIpgSystemId]: cardPointeCustomizationSchema,
        [CloverIpgSystemId]: cloverCustomizationSchema,
    };
    const customizationMapSchema: z.ZodType<IpgConfigCustomizationMap> = z
        .object({
            [ValorIpgSystemIdKey]: valorCustomizationSchema,
            [CardPointeIpgSystemIdKey]: cardPointeCustomizationSchema,
            [CloverIpgSystemIdKey]: cloverCustomizationSchema,
        })
        .partial()
        .strict();
    const updateCustomizationMapSchema: z.ZodType<UpdateIpgConfigCustomizationMap> = z
        .object({
            [ValorIpgSystemIdKey]: valorCustomizationSchema.nullable(),
            [CardPointeIpgSystemIdKey]: cardPointeCustomizationSchema.nullable(),
            [CloverIpgSystemIdKey]: cloverCustomizationSchema.nullable(),
        })
        .partial()
        .strict();

    return {
        schema,
        schemaById,
        storageMapSchema,
        storageMapV4Schema,
        storageMapV6Schema,
        storageMapV7Schema,
        customizationSchema,
        customizationSchemaById,
        customizationMapSchema,
        updateCustomizationMapSchema,
    };
}

const LegacyCloverProductionApiUrl = 'https://api.clover.com/connect';
const LegacyCloverSandboxApiUrl = 'https://apisandbox.dev.clover.com/connect';

const PlainRecordSchema: z.ZodRecord<z.ZodString, z.ZodUnknown> = z.record(z.string(), z.unknown());

function isPlainRecord(value: unknown): value is Record<string, unknown> {
    return PlainRecordSchema.safeParse(value).success;
}

export function validateLegacyIpgConfigMap(
    config: { readonly ipg?: unknown },
    ctx: z.RefinementCtx
): void {
    if (!isPlainRecord(config.ipg)) {
        return;
    }

    for (const systemKey of [
        ValorIpgSystemIdKey,
        CardPointeIpgSystemIdKey,
        CloverIpgSystemIdKey,
    ] as const) {
        const systemConfig = config.ipg[systemKey];
        if (!isPlainRecord(systemConfig)) {
            continue;
        }
        if (systemKey !== ValorIpgSystemIdKey && systemConfig.default_channel !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ipg', systemKey, 'default_channel'],
                message: 'Legacy non-Valor default_channel cannot be normalized.',
            });
        }
        if (systemKey === CardPointeIpgSystemIdKey && systemConfig.default_api_url !== undefined) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ipg', systemKey, 'default_api_url'],
                message: 'Legacy CardPointe default_api_url cannot be normalized.',
            });
        }
        if (
            systemKey === CloverIpgSystemIdKey &&
            systemConfig.default_api_url !== undefined &&
            systemConfig.default_api_url !== LegacyCloverProductionApiUrl &&
            systemConfig.default_api_url !== LegacyCloverSandboxApiUrl
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ipg', systemKey, 'default_api_url'],
                message: 'Legacy Clover URL is not a supported environment.',
            });
        }
    }
}

export function normalizeLegacyIpgConfigMap(value: unknown): unknown {
    if (!isPlainRecord(value)) {
        return value;
    }

    const normalizedConfigMap: Record<string, unknown> = { ...value };
    for (const systemKey of [
        ValorIpgSystemIdKey,
        CardPointeIpgSystemIdKey,
        CloverIpgSystemIdKey,
    ] as const) {
        const systemConfig = value[systemKey];
        if (!isPlainRecord(systemConfig)) {
            continue;
        }
        if (systemKey === ValorIpgSystemIdKey) {
            normalizedConfigMap[systemKey] = systemConfig;
            continue;
        }
        if (systemKey === CardPointeIpgSystemIdKey) {
            normalizedConfigMap[systemKey] = systemConfig;
            continue;
        }
        if (
            systemConfig.environment !== undefined ||
            systemConfig.terminal_transport !== undefined ||
            systemConfig.default_channel !== undefined
        ) {
            normalizedConfigMap[systemKey] = systemConfig;
            continue;
        }
        const { default_api_url: legacyDefaultApiUrl, ...baseConfig } = systemConfig;
        if (
            legacyDefaultApiUrl !== undefined &&
            legacyDefaultApiUrl !== LegacyCloverProductionApiUrl &&
            legacyDefaultApiUrl !== LegacyCloverSandboxApiUrl
        ) {
            normalizedConfigMap[systemKey] = systemConfig;
            continue;
        }
        normalizedConfigMap[systemKey] = {
            ...baseConfig,
            environment:
                legacyDefaultApiUrl === LegacyCloverSandboxApiUrl
                    ? CloverGatewayEnvironment.SANDBOX
                    : CloverGatewayEnvironment.PRODUCTION,
            terminal_transport: CloverTerminalTransport.CLOUD_PAY_DISPLAY,
        };
    }
    return normalizedConfigMap;
}
